<template>
  <section class="documents-page">
    <TopBarTitleComponent title="Ventas" :showBackIcon="true">
      <template slot="right-area">
        <div class="d-flex justify-content-end align-items-center">
          <!-- Toggle vista: columnas / lista -->
          <div v-if="!isMobile" class="view-toggle mr-3">
            <button
              type="button"
              :class="['view-btn', { active: viewMode === 'board' }]"
              @click="viewMode = 'board'"
              title="Vista en columnas"
            >
              <i class="el-icon-s-grid"></i>
            </button>
            <button
              type="button"
              :class="['view-btn', { active: viewMode === 'list' }]"
              @click="viewMode = 'list'"
              title="Vista en lista"
            >
              <i class="el-icon-s-unfold"></i>
            </button>
          </div>
          <FilterDocumentShowComponent class="mr-2" />
          <!-- Firmado Masivo — abre el módulo Next.js con auth handoff -->
          <el-tooltip
            v-if="!isMobile"
            content="Firmado Masivo"
            placement="bottom"
            effect="dark"
          >
            <button
              type="button"
              class="btn btn-firmado-masivo mr-2"
              @click="openFirmadoMasivo"
            >
              <i class="el-icon-files"></i>
            </button>
          </el-tooltip>
          <ButtonAddSaleDocumentsComponent />
        </div>
      </template>
    </TopBarTitleComponent>

    <div class="wrapper-sale-documents mt-3">
      <div class="wrapper-sale-documents-content px-4">
        <div class="wrapper-sale-documents-main">
          <!-- Pills: móvil siempre, desktop solo en vista lista -->
          <div
            v-if="isMobile || viewMode === 'list'"
            class="select-column-container"
          >
            <div class="column-pills">
              <button
                v-for="col in visibleColumnOptions"
                :key="col.value"
                type="button"
                :class="[
                  'column-pill',
                  { active: selectedColumns === col.value }
                ]"
                @click="selectedColumns = col.value"
              >
                {{ col.label }}
              </button>
            </div>
          </div>

          <!-- ═══ VISTA BOARD (columnas) ═══ -->
          <div
            v-if="!isMobile && viewMode === 'board'"
            class="wrapper__content-sale-board"
          >
            <div
              ref="boardEl"
              class="board board--no-scrollbar"
              @scroll="onBoardScroll"
              @wheel="onBoardWheel"
            >
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility('is_sales_quotes_visibility')
                "
              >
                <div id="tour-step-0"></div>
                <QuoteColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility('is_sales_invoices_visibility')
                "
              >
                <div id="tour-step-1"></div>
                <InvoiceColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="$store.getters.hasPermission('expenses.view')"
              >
                <ExpenseColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility(
                    'is_sales_credit_notes_visibility'
                  )
                "
              >
                <div id="tour-step-2"></div>
                <CreditNoteColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility(
                    'is_sales_debit_notes_visibility'
                  )
                "
              >
                <div id="tour-step-3"></div>
                <DebitNoteColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility(
                    'is_sales_liquidations_visibility'
                  )
                "
              >
                <LiquidationColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility(
                    'is_sales_withholdings_visibility'
                  )
                "
              >
                <div id="tour-step-4"></div>
                <WithholdingColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility('is_sales_payments_visibility')
                "
              >
                <PaymentColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility(
                    'is_sales_remissions_visibility'
                  )
                "
              >
                <RemissionColumnsComponent />
              </div>
              <div
                class="wrapper-list-documents"
                v-if="
                  toggleSalesDocumentsVisibility(
                    'is_sales_invoices_api_visibility'
                  )
                "
              >
                <InvoiceApiColumnsComponent />
              </div>
            </div>
            <!-- ── Minimap scroll indicator ── -->
            <div class="board-minimap" @mousedown="onMinimapMouseDown">
              <div class="board-minimap__track">
                <div
                  v-for="(col, i) in visibleColumnOptions"
                  :key="col.value"
                  class="board-minimap__col"
                  :class="{ 'board-minimap__col--visible': isColumnVisible(i) }"
                />
                <div class="board-minimap__thumb" :style="minimapThumbStyle" />
              </div>
            </div>
          </div>

          <!-- ═══ VISTA LISTA (tabs + franjas horizontales) ═══ -->
          <div
            v-if="isMobile || viewMode === 'list'"
            class="wrapper__content-sale-list"
            :class="{ 'list-row-mode': !isMobile && viewMode === 'list' }"
          >
            <div class="list-view">
              <!-- Header de tabla para vista lista desktop -->
              <div
                v-if="!isMobile && viewMode === 'list'"
                class="list-table-header"
              >
                <span class="col-number">N°</span>
                <span class="col-client">Cliente</span>
                <span class="col-amount">Monto</span>
                <span class="col-date">Fecha</span>
                <span class="col-status">Estado</span>
                <span class="col-actions"></span>
              </div>

              <div v-if="selectedColumns === 'quote'">
                <QuoteColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'invoice'">
                <InvoiceColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'expense'">
                <ExpenseColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'credit-note'">
                <CreditNoteColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'debit-note'">
                <DebitNoteColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'liquidation'">
                <LiquidationColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'withholding'">
                <WithholdingColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'payment'">
                <PaymentColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'invoiceApi'">
                <InvoiceApiColumnsComponent />
              </div>
              <div v-if="selectedColumns === 'remission'">
                <RemissionColumnsComponent />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <WrapperSalesTourComponent />
  </section>
</template>

<script>
import TopBarTitleComponent from "../../components/TopBarTitle";

// Preferencia de visibilidad de cada columna → permiso requerido para verla.
const VIS_KEY_PERMISSION = {
  is_sales_quotes_visibility: "quotes.view",
  is_sales_invoices_visibility: "invoices.view",
  is_sales_invoices_api_visibility: "invoices.view",
  is_sales_credit_notes_visibility: "credit_notes.view",
  is_sales_debit_notes_visibility: "debit_notes.view",
  is_sales_liquidations_visibility: "liquidations.view",
  is_sales_withholdings_visibility: "withholdings.view",
  is_sales_payments_visibility: "payments.view",
  is_sales_remissions_visibility: "remissions.view"
};
import QuoteColumnsComponent from "./quotes/components/QuoteColumns";
import InvoiceColumnsComponent from "./invoices/components/InvoiceColumns";
import InvoiceApiColumnsComponent from "./invoices/components/InvoiceApiColumns";
import CreditNoteColumnsComponent from "./credit-note/components/CreditNoteColumns";
import DebitNoteColumnsComponent from "./debit-note/components/DebitNoteColumns";
import WithholdingColumnsComponent from "./withholdings/components/WithholdingColumns";
import PaymentColumnsComponent from "./payments/components/PaymentColumns";
import ButtonAddSaleDocumentsComponent from "../components/ButtonAddDocuments";
import WrapperSalesTourComponent from "../components/WrapperTour";
import FilterDocumentShowComponent from "../components/FilterDocumentShow";
import LiquidationColumnsComponent from "./liquidation/components/LiquidationColumns";
import RemissionColumnsComponent from "./remission/components/RemissionColumns";
import ExpenseColumnsComponent from "./expenses/components/ExpenseColumns";
export default {
  name: "SalesDocumentsPage",
  data() {
    return {
      clientId: null,
      viewMode: "board",
      selectedColumns: "quote",
      boardScrollRatio: 0,
      boardViewRatio: 1,
      allColumnOptions: [
        {
          value: "quote",
          label: "Cotizaciones",
          visKey: "is_sales_quotes_visibility"
        },
        {
          value: "invoice",
          label: "Facturas",
          visKey: "is_sales_invoices_visibility"
        },
        {
          value: "expense",
          label: "Gastos",
          visKey: null
        },
        {
          value: "credit-note",
          label: "N. Crédito",
          visKey: "is_sales_credit_notes_visibility"
        },
        {
          value: "debit-note",
          label: "N. Débito",
          visKey: "is_sales_debit_notes_visibility"
        },
        {
          value: "payment",
          label: "Pagos",
          visKey: "is_sales_payments_visibility"
        },
        {
          value: "liquidation",
          label: "Liquidaciones",
          visKey: "is_sales_liquidations_visibility"
        },
        {
          value: "withholding",
          label: "Retenciones",
          visKey: "is_sales_withholdings_visibility"
        },
        {
          value: "remission",
          label: "Remisiones",
          visKey: "is_sales_remissions_visibility"
        },
        {
          value: "invoiceApi",
          label: "API",
          visKey: "is_sales_invoices_api_visibility"
        }
      ],
      isMobile: false // eslint-disable-line
    };
  },
  computed: {
    getUser() {
      return this.$store.getters.getUser;
    },
    visibleColumnOptions() {
      return this.allColumnOptions.filter(opt =>
        opt.visKey === null
          ? this.$store.getters.hasPermission("expenses.view")
          : this.toggleSalesDocumentsVisibility(opt.visKey)
      );
    },
    minimapThumbStyle() {
      const w = Math.max(this.boardViewRatio * 100, 15);
      const maxLeft = 100 - w;
      const left = this.boardScrollRatio * maxLeft;
      return {
        width: `${w}%`,
        left: `${left}%`
      };
    },
    firmadoMasivoUrl() {
      const override = process.env.VUE_APP_FIRMADO_MASIVO_URL;
      if (override) return override.replace(/\/firmado-masivo\/?$/, "");
      if (
        typeof window !== "undefined" &&
        window.location.host.indexOf("app.mydessk.com") !== -1
      ) {
        return (
          "https://" +
          window.location.host.replace(
            "app.mydessk.com",
            "marketing.mydessk.com"
          )
        );
      }
      return "";
    }
  },
  methods: {
    openFirmadoMasivo() {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user && user.token ? user.token : "";
      const businessId = this.$store.getters.getUser?.user_business_id || "";
      const next =
        "/firmado-masivo" + (businessId ? "?business_id=" + businessId : "");
      const base = this.firmadoMasivoUrl;
      if (!token) {
        window.location.href = (base || "") + next;
        return;
      }
      window.location.href =
        (base || "") +
        "/auth/handoff#token=" +
        encodeURIComponent(token) +
        "&next=" +
        encodeURIComponent(next);
    },
    toggleSalesDocumentsVisibility(key) {
      // Multi-usuario: sin permiso de ver el tipo, la columna no se muestra
      // (evita listar endpoints que responderían 403).
      const permission = VIS_KEY_PERMISSION[key];
      if (permission && !this.$store.getters.hasPermission(permission)) {
        return false;
      }
      const { user_config_general } = this.getUser;
      if (user_config_general && user_config_general.hasOwnProperty(key))
        return user_config_general[key];
      else return true;
    },
    checkMobileView() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.viewMode = "list";
      }
    },
    onBoardWheel(e) {
      // Forward horizontal wheel (shift+wheel or trackpad swipe) to the board
      const dx = e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (Math.abs(dx) > Math.abs(e.shiftKey ? 0 : e.deltaY)) {
        this.$refs.boardEl.scrollLeft += dx;
        e.preventDefault();
      }
    },
    onBoardScroll() {
      const el = this.$refs.boardEl;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      this.boardScrollRatio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
      this.boardViewRatio =
        el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1;
    },
    isColumnVisible(index) {
      const el = this.$refs.boardEl;
      if (!el) return false;
      const colWidth = 378; // 370 + 8 margin
      const colLeft = index * colWidth;
      const colRight = colLeft + colWidth;
      const viewLeft = el.scrollLeft;
      const viewRight = viewLeft + el.clientWidth;
      return colRight > viewLeft && colLeft < viewRight;
    },
    onMinimapMouseDown(e) {
      const track = e.currentTarget.querySelector(".board-minimap__track");
      if (!track) return;
      const scrollToPos = pos => {
        const el = this.$refs.boardEl;
        if (!el) return;
        const rect = track.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (pos - rect.left) / rect.width));
        const maxScroll = el.scrollWidth - el.clientWidth;
        el.scrollLeft = ratio * maxScroll;
      };
      scrollToPos(e.clientX);
      const onMove = ev => scrollToPos(ev.clientX);
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    }
  },
  watch: {
    // Si el tipo seleccionado no está permitido, cae al primero visible.
    visibleColumnOptions: {
      immediate: true,
      handler(options) {
        if (
          options.length &&
          !options.some(opt => opt.value === this.selectedColumns)
        ) {
          this.selectedColumns = options[0].value;
        }
      }
    },
    $route() {
      this.checkMobileView();
    },
    viewMode(val) {
      if (!this.isMobile) {
        this.$store.dispatch("setViewPreference", {
          key: "salesDocuments",
          value: val
        });
      }
    }
  },
  components: {
    TopBarTitleComponent,
    ButtonAddSaleDocumentsComponent,
    QuoteColumnsComponent,
    WrapperSalesTourComponent,
    InvoiceColumnsComponent,
    PaymentColumnsComponent,
    CreditNoteColumnsComponent,
    DebitNoteColumnsComponent,
    WithholdingColumnsComponent,
    FilterDocumentShowComponent,
    LiquidationColumnsComponent,
    InvoiceApiColumnsComponent,
    RemissionColumnsComponent,
    ExpenseColumnsComponent
  },
  mounted() {
    // Las columnas ya cargan al montarse. Limpiar la bandera evita que quede
    // pegada en true cuando quien la levantó (p. ej. convertir una cotización
    // en factura) navegó fuera antes de que alguna columna la consumiera —
    // si se quedara en true, el siguiente aviso no sería un cambio y nadie
    // se refrescaría.
    this.$store.dispatch("sendNotification", false);
    this.checkMobileView();
    // Restaurar preferencia de vista (solo desktop)
    if (!this.isMobile) {
      const saved = this.$store.getters.getViewPreference("salesDocuments");
      if (saved) this.viewMode = saved;
    }
    window.addEventListener("resize", this.checkMobileView);
    this.$nextTick(() => this.onBoardScroll());
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkMobileView);
  }
};
</script>

<style scoped>
.btn-firmado-masivo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 8px;
  border: 1.5px solid #2d2b55;
  background: transparent;
  color: #2d2b55;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-firmado-masivo:hover {
  background: #2d2b55;
  color: #fff;
}

/* ── Page layout: body estático, solo columnas scrollean ── */
.documents-page {
  height: calc(100vh - 64px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ── View toggle ── */
.view-toggle {
  display: flex;
  border: 1px solid #e4eaf6;
  border-radius: 8px;
  overflow: hidden;
}
.view-btn {
  background: #fff;
  border: none;
  padding: 6px 10px;
  font-size: 16px;
  color: #909399;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1;
}
.view-btn:hover {
  color: #31355c;
}
.view-btn.active {
  background: #31355c;
  color: #fff;
}
.view-btn + .view-btn {
  border-left: 1px solid #e4eaf6;
}

/* ── Column pills ── */
.select-column-container {
  margin-bottom: 12px;
  padding: 0 4px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: transparent;
}
.column-pills {
  display: flex;
  overflow-x: auto;
  gap: 6px;
  padding: 4px 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.column-pills::-webkit-scrollbar {
  display: none;
}
.column-pill {
  flex-shrink: 0;
  padding: 7px 16px;
  border-radius: 20px;
  border: 1px solid #e4eaf6;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #909399;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.column-pill:hover {
  border-color: #3fdbbf;
  color: #31355c;
}
.column-pill.active {
  background: #31355c;
  border-color: #31355c;
  color: #fff;
  font-weight: 600;
}

/* ── List view ── */
.list-view {
  max-width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
}
.wrapper__content-sale-list {
  flex-grow: 1;
  overflow: hidden;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Mobile: sticky document header so filters stay visible while scrolling */
@media (max-width: 768px) {
  .wrapper__content-sale-list .sale-document-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fff;
    padding-top: 8px;
    margin-top: 0;
  }
}

.mobile-view {
  overflow: hidden;
}

/* ── Minimap scroll indicator ── */
.board-minimap {
  display: flex;
  justify-content: center;
  padding: 6px 0 2px;
  cursor: pointer;
  user-select: none;
}
.board-minimap__track {
  position: relative;
  display: flex;
  gap: 3px;
  background: #f0f2f7;
  border-radius: 6px;
  padding: 4px 6px;
  max-width: 420px;
  width: 100%;
}
.board-minimap__col {
  flex: 1;
  height: 22px;
  border-radius: 3px;
  background: #e4e8f0;
  transition: background 0.15s ease;
}
.board-minimap__col--visible {
  background: #c8cdd8;
}
.board-minimap__thumb {
  position: absolute;
  top: 2px;
  bottom: 2px;
  border-radius: 5px;
  background: rgba(49, 53, 92, 0.13);
  border: 1.5px solid rgba(49, 53, 92, 0.25);
  pointer-events: none;
  transition: left 0.08s ease, width 0.08s ease;
}
</style>

<!-- Estilos NO scoped para penetrar componentes hijos en vista lista -->
<style lang="scss">
// ═══ TABLE HEADER ═══
.list-table-header {
  display: grid;
  grid-template-columns: 110px 1fr 120px 110px 130px 48px;
  align-items: center;
  padding: 10px 20px;
  background: #f7f8f9;
  border-bottom: 1px solid #e4eaf6;
  border-radius: 8px 8px 0 0;
  position: sticky;
  top: 0;
  z-index: 5;
  span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #6b7280;
  }
  .col-amount,
  .col-date {
    text-align: right;
  }
  .col-status {
    text-align: center;
  }
}

// ═══ Reorder: filters above table header ═══
// Collapse wrappers so header, table-header, and scroll container
// become direct flex children of .list-view for reordering
.list-row-mode .list-view > div:not(.list-table-header) {
  display: contents !important;
}
.list-row-mode .wrapper__content-sale-document {
  display: contents !important;
}
// Remove col-container visual in list mode
.list-row-mode .col-container {
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
}
.list-row-mode .sale-document-header,
.list-row-mode .col-header {
  order: 1 !important;
}
.list-row-mode .list-table-header {
  order: 2 !important;
}
.list-row-mode .wrapper-scrollbar-container {
  order: 3 !important;
  // Cada card-wrapper hijo directo recibe el border-bottom
  > div,
  > section {
    border-bottom: 1px solid #eef1f7;
    &:last-child {
      border-bottom: none;
    }
  }
}

// ═══ Header interno en list mode: ocultar título, filtros inline ═══
.list-row-mode .sale-document-header,
.list-row-mode .col-header {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding: 8px 20px !important;
  background: transparent !important;
  display: flex !important;
  justify-content: flex-start !important;
  align-items: center !important;
  gap: 8px;
  h4 {
    display: none !important;
  }
  .col-title {
    display: none !important;
  }
  .icons,
  .col-actions {
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 8px;
  }
  .col-header-left {
    display: flex !important;
    align-items: center !important;
    gap: 8px;
  }
}

// ═══ CARD → FILA ═══
.list-row-mode
  .sale-document-body.md__border.border-radius.bg-color-write.p-3.mb-2,
.list-row-mode .sale-document-body {
  display: grid !important;
  grid-template-columns: 110px 1fr 120px 110px 130px 48px !important;
  align-items: center !important;
  border-radius: 0 !important;
  margin: 0 !important;
  padding: 12px 20px !important;
  box-shadow: none !important;
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #eef1f7 !important;
  background: #fff !important;
  cursor: pointer;
  transition: background 0.12s ease !important;
  &:hover {
    transform: none !important;
    box-shadow: none !important;
    background: #f5f7fb !important;
  }
  &:last-child {
    border-bottom: none !important;
  }
}

// ═══ Ocultar avatar ═══
.list-row-mode .sale-document-description .avatar {
  display: none !important;
}

// ═══ INFO: grid-aware layout ═══
.list-row-mode .sale-document-info {
  display: contents !important;
}
.list-row-mode .sale-document-description.d-flex.justify-content-between,
.list-row-mode .sale-document-description {
  display: contents !important;
}
.list-row-mode .sale-document-description .info.cursor-point,
.list-row-mode .sale-document-description .info {
  display: contents !important;
  p {
    margin: 0 !important;
    font-size: 13.5px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    line-height: 1.4 !important;
  }
  // N° documento
  p:first-child {
    font-weight: 600 !important;
    color: #31355c !important;
    font-size: 13px !important;
  }
  // Cliente
  p:nth-child(2) {
    font-weight: 500 !important;
    color: #4b5563 !important;
    padding-right: 12px !important;
  }
  // Ocultar párrafos extra (cotización ref, etc.)
  p:nth-child(n + 3) {
    display: none !important;
  }
}

// ═══ MONTO + FECHA ═══
.list-row-mode .sale-document-number {
  display: contents !important;
  h5,
  h5.h5.font-bold {
    font-size: 13.5px !important;
    font-weight: 700 !important;
    margin: 0 !important;
    color: #1f2937 !important;
    text-align: right !important;
  }
  .card-date {
    font-size: 13px !important;
    color: #6b7280 !important;
    text-align: right !important;
    display: block !important;
  }
}

// ═══ ACCIONES (Estado + menú) ═══
.list-row-mode
  .sale-document-actions.d-flex.justify-content-between.align-items-center.mt-4,
.list-row-mode .sale-document-actions {
  display: contents !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;

  .status,
  .status.d-flex {
    grid-column: 5 !important;
    grid-row: 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 4px !important;
    overflow: hidden !important;
    position: relative !important;
    // Ocultar elementos extra en modo lista (woocommerce, crédito, reminders)
    > div.ml-2,
    > div:not(.next):not(:first-child):not(span):not(p) {
      display: none !important;
    }
    > .el-switch,
    .el-tooltip .el-switch {
      display: none !important;
    }
    p,
    p.m-0.font-bold.text-color-primary {
      margin: 0 !important;
      font-size: 0 !important;
      display: flex !important;
      align-items: center !important;
      gap: 4px !important;
      .el-tag {
        font-size: 11px !important;
        padding: 2px 10px !important;
        height: auto !important;
        line-height: 1.6 !important;
        border-radius: 12px !important;
      }
    }
    // CheckSuccessfully (Pagada badge) — hacerlo compacto
    > span:not(.el-tag):not(.el-tooltip) {
      font-size: 11px !important;
      white-space: nowrap !important;
      img {
        width: 14px !important;
      }
    }
    .ico-version,
    img[alt="Email"],
    img[alt="Versiones"] {
      display: none !important;
    }
  }
}

// .next → grid item en columna 6 (acciones)
.list-row-mode .next {
  grid-column: 6 !important;
  grid-row: 1 !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  position: static !important;
  transform: none !important;
  width: auto !important;
}

// ═══ Menu dots — always visible ═══
.list-row-mode #icon-more-option {
  opacity: 0.5;
  transition: opacity 0.15s;
}
.list-row-mode .sale-document-body:hover #icon-more-option {
  opacity: 1;
}

// ═══ Zebra striping for alternating rows ═══
.list-row-mode .sale-document-body:nth-child(even) {
  background: #fafbfd !important;
  &:hover {
    background: #f5f6fa !important;
  }
}

// ═══ Scroll container ═══
.list-row-mode .wrapper-scrollbar-container {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  border: none;
  border-radius: 0;
}

// ═══ MOBILE ═══
@media (max-width: 768px) {
  .list-table-header {
    display: none !important;
  }
  .list-row-mode
    .sale-document-body.md__border.border-radius.bg-color-write.p-3.mb-2,
  .list-row-mode .sale-document-body {
    display: flex !important;
    flex-direction: column !important;
    padding: 14px 16px !important;
    border-radius: 10px !important;
    margin-bottom: 8px !important;
    border: 1px solid #e8ecf2 !important;
    border-bottom: 1px solid #e8ecf2 !important;
    box-shadow: 0 1px 3px rgba(49, 53, 92, 0.04) !important;
    &:hover {
      background: #fff !important;
    }
  }
  .list-row-mode .sale-document-info {
    display: block !important;
    width: 100% !important;
  }
  .list-row-mode .sale-document-description.d-flex.justify-content-between,
  .list-row-mode .sale-document-description {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    width: 100% !important;
  }
  .list-row-mode .sale-document-description .info.cursor-point,
  .list-row-mode .sale-document-description .info {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 !important;
    min-width: 0 !important;
    gap: 2px !important;
    p:first-child {
      font-size: 12px !important;
      color: #6b7280 !important;
      font-weight: 500 !important;
    }
    p:nth-child(2) {
      font-size: 14px !important;
      font-weight: 600 !important;
      color: #1f2937 !important;
      padding-right: 8px !important;
    }
  }
  .list-row-mode .sale-document-number {
    display: flex !important;
    flex-direction: column !important;
    align-items: flex-end !important;
    flex-shrink: 0 !important;
    h5,
    h5.h5.font-bold {
      font-size: 14px !important;
      text-align: right !important;
    }
    .card-date {
      font-size: 12px !important;
      text-align: right !important;
    }
  }
  .list-row-mode
    .sale-document-actions.d-flex.justify-content-between.align-items-center.mt-4,
  .list-row-mode .sale-document-actions {
    display: flex !important;
    width: 100% !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-top: 10px !important;
    padding-top: 10px !important;
    border-top: 1px solid #f0f2f7 !important;
    .status,
    .status.d-flex {
      width: auto !important;
    }
    .next {
      width: auto !important;
      position: static !important;
    }
  }
  .list-row-mode .wrapper-scrollbar-container {
    max-height: none !important;
    border: none !important;
    border-radius: 0 !important;
  }
}

// ═══ TABLET ═══
@media (max-width: 1200px) and (min-width: 769px) {
  .list-table-header {
    grid-template-columns: 90px 1fr 100px 100px 110px 44px !important;
  }
  .list-row-mode
    .sale-document-body.md__border.border-radius.bg-color-write.p-3.mb-2,
  .list-row-mode .sale-document-body {
    grid-template-columns: 90px 1fr 100px 100px 110px 44px !important;
  }
}
</style>
